import React, { useState, useEffect } from "react";
import { syncOpenCircDatasets } from "../../../../../apis/OpenCircApi";
import Swal from "sweetalert2";
import { Sync, KeyboardArrowDown, Lock, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Select, MenuItem, FormControl, Menu } from "@mui/material";
import "./OpenCircSyncButton.css";

// Lock status: "unlocked" | "calc_ended" | "epd_locked" – from product/backend when available
// sector: from product's sector (product detail)
const OpenCircSyncButton = ({ productId, onSyncComplete, lockStatus = "unlocked", sector: productSectorProp = "" }) => {
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [calculationFramework, setCalculationFramework] = useState("EN15804_GENERIC");
  const [databaseAnchorEl, setDatabaseAnchorEl] = useState(null);
  const [productSector, setProductSector] = useState(productSectorProp || "");

  // Keep sector in sync with product's sector from backend
  useEffect(() => {
    setProductSector(productSectorProp || "");
  }, [productSectorProp]);

  const handleSync = () => {
    if (!productId) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Product ID is missing",
      });
      return;
    }

    setSyncing(true);

    syncOpenCircDatasets({
      productId,
      containerId: null, // Will be fetched from product.opencirc_passport_id or auto-created
      onSuccess: (result) => {
        setSyncing(false);
        setLastSyncTime(new Date());

        // Build success message
        let message = "";
        let icon = "success";
        
        if (result.passport_created) {
          message = "OpenCirc passport oprettet automatisk. ";
        }
        
        if (result.pending_count > 0) {
          message += `${result.pending_count} nye datasæt fundet`;
          icon = "success";
        } else {
          message += "Ingen nye datasæt fundet";
          icon = "info";
        }

        Swal.fire({
          icon: icon,
          title: icon === "success" ? "Success" : "Info",
          text: message,
          timer: icon === "success" ? 3000 : 2000,
          showConfirmButton: false,
        });

        // Call callback to update component rows
        if (onSyncComplete) {
          onSyncComplete(result);
        }
        // Dispatch custom event to trigger table refresh
        window.dispatchEvent(new CustomEvent("opencirc-sync-complete", { detail: result }));
      },
      onError: (error) => {
        setSyncing(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Kunne ikke synkronisere: ${error}`,
        });
      },
    });
  };

  const formatTime = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("da-DK", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const handleDatabaseMenuOpen = (event) => {
    setDatabaseAnchorEl(event.currentTarget);
  };

  const handleDatabaseMenuClose = () => {
    setDatabaseAnchorEl(null);
  };

  const handleDatabaseChange = (database) => {
    // Database change functionality can be implemented here
    handleDatabaseMenuClose();
  };

  return (
    <div className="opencirc-sync-wrapper">
      {/* Explanation */}
      <div className="opencirc-sync-explanation">
        <p className="opencirc-sync-title">Product Calculation</p>
        <p className="opencirc-sync-description">
          Describe your product step by step. Add materials, transport, use and post-use. The system will calculate the impact and show the results collectively. It is up to you if you want to work with a Product carbon footprint according to the GHG protocol, a Life Cycle Analysis (LCA) according to ISO standards or you want to aim for Circularity and move your products to the next level.
        </p>
      </div>

      {/* Control group */}
      <div className="opencirc-sync-controls">
        {/* Standard */}
        <div className="opencirc-sync-control-item">
          <span className="opencirc-sync-control-label">Standard</span>
          <FormControl size="small" className="opencirc-sync-select-wrapper">
            <Select
              value={calculationFramework}
              onChange={(e) => setCalculationFramework(e.target.value)}
              className="opencirc-sync-select"
              displayEmpty
            >
              <MenuItem value="EN15804_GENERIC">EN 15804 / EN 15978 – Generic LCA</MenuItem>
              <MenuItem value="BR18_TABLE6">BR18 – Tabel 6 (Compliance)</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Divider */}
        <div className="opencirc-sync-divider" />

        {/* Change Database */}
        <div className="opencirc-sync-control-item">
          <span className="opencirc-sync-control-label">Database</span>
          <button
            type="button"
            onClick={handleDatabaseMenuOpen}
            className="opencirc-sync-button"
          >
            Select
          </button>
          <Menu
            anchorEl={databaseAnchorEl}
            open={Boolean(databaseAnchorEl)}
            onClose={handleDatabaseMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                zIndex: 200,
                borderRadius: '8px',
                marginTop: '4px',
                minWidth: '200px',
              }
            }}
          >
            <MenuItem onClick={() => handleDatabaseChange("bonsai")} sx={{ fontSize: '12px' }}>BONSAI</MenuItem>
            <MenuItem onClick={() => handleDatabaseChange("idemat")} sx={{ fontSize: '12px' }}>IDEMAT</MenuItem>
            <MenuItem onClick={() => handleDatabaseChange("ecoinvent")} sx={{ fontSize: '12px' }}>ECOINVENT</MenuItem>
            <MenuItem onClick={() => handleDatabaseChange("br18")} sx={{ fontSize: '12px' }}>BR 18</MenuItem>
          </Menu>
        </div>

        {/* Divider */}
        <div className="opencirc-sync-divider" />

        {/* Synkroniser OpenCirc */}
        <div className="opencirc-sync-control-item">
          <span className="opencirc-sync-control-label">OpenCirc</span>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="opencirc-sync-button"
          >
            {syncing ? (
              <>
                <Sync className="opencirc-sync-icon spinning" />
                Synkroniserer…
              </>
            ) : (
              <>
                <Sync className="opencirc-sync-icon" />
                Synkroniser OpenCirc
                <KeyboardArrowDown className="opencirc-sync-chevron" />
              </>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="opencirc-sync-divider" />

        {/* Sector – from product's sector */}
        <div className="opencirc-sync-control-item">
          <span className="opencirc-sync-control-label">Sector</span>
          <FormControl size="small" className="opencirc-sync-select-wrapper">
            <Select
              value={productSector}
              onChange={(e) => setProductSector(e.target.value)}
              className="opencirc-sync-select"
              displayEmpty
            >
              <MenuItem value="">Select Sector</MenuItem>
              <MenuItem value="construction">Construction</MenuItem>
              <MenuItem value="manufacturing">Manufacturing</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="food">Food</MenuItem>
              <MenuItem value="textiles">Textiles</MenuItem>
              <MenuItem value="electronics">Electronics</MenuItem>
              <MenuItem value="furniture">Furniture</MenuItem>
              <MenuItem value="packaging">Packaging</MenuItem>
              <MenuItem value="consumer">Consumer</MenuItem>
              <MenuItem value="generic">Generic</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Divider */}
        <div className="opencirc-sync-divider" />

        {/* Lock status switch – A Unlocked, B Calc. Ended, C EPD Locked (auto-chosen by product EPD state) */}
        <div className="opencirc-sync-control-item opencirc-sync-lock-group">
          <span className="opencirc-sync-control-label">LOCK</span>
          <div className="opencirc-sync-lock-buttons">
            <span
              className={`opencirc-sync-lock-pill ${lockStatus === "unlocked" ? "opencirc-sync-lock-pill-active" : ""}`}
              title="A Unlocked"
            >
              <Lock sx={{ fontSize: 14 }} className="opencirc-sync-lock-icon" />
              <span>A Unlocked</span>
            </span>
            <span
              className={`opencirc-sync-lock-pill ${lockStatus === "calc_ended" ? "opencirc-sync-lock-pill-active" : ""}`}
              title="B Calc. Ended"
            >
              <CheckBoxOutlineBlank sx={{ fontSize: 14 }} className="opencirc-sync-lock-icon" />
              <span>B Calc. Ended</span>
            </span>
            <span
              className={`opencirc-sync-lock-pill opencirc-sync-lock-pill-epd ${lockStatus === "epd_locked" ? "opencirc-sync-lock-pill-epd-active" : ""}`}
              title="C EPD Locked"
            >
              <Lock sx={{ fontSize: 14 }} className="opencirc-sync-lock-icon" />
              <span>C EPD Locked</span>
            </span>
          </div>
        </div>
      </div>

      {lastSyncTime && (
        <div className="opencirc-sync-last-sync">
          <span className="opencirc-sync-last-sync-label">Sidst synkroniseret:</span>
          <span className="opencirc-sync-last-sync-time">{formatTime(lastSyncTime)}</span>
        </div>
      )}
    </div>
  );
};

export default OpenCircSyncButton;

